import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Target, Truck, Sparkles, HardHat, Star } from "lucide-react";
import { useI18n } from "@/i18n";
import { IMAGES } from "@/lib/media";

const TYPES = [
  { key: "industriel", image: IMAGES.maintenance_industriel, features: ["f1", "f2", "f3", "f4", "f5"] },
  { key: "commercial", image: IMAGES.maintenance_commercial, features: ["f1", "f2", "f3", "f4", "f5"] },
  { key: "residentiel", image: IMAGES.maintenance_residentiel, features: ["f1", "f2", "f3", "f4", "f5", "f6"] },
];

const PLANS = [
  { key: "silver", accent: "#A8A29E", ring: "border-stone-300", chip: "bg-stone-100 text-stone-700" },
  { key: "gold", accent: "#B95736", ring: "border-[#B95736]", chip: "bg-[#B95736]/10 text-[#B95736]", featured: true },
  { key: "premium", accent: "#1C1917", ring: "border-stone-900", chip: "bg-stone-900 text-white" },
];

const DETAILS = [
  { key: "item1", icon: Truck },
  { key: "item2", icon: Sparkles },
  { key: "item3", icon: HardHat },
];

export default function ServiceMaintenance() {
  const { t } = useI18n();

  return (
    <div data-testid="page-service-maintenance">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#B95736] font-mono">{t("maint.hero.eyebrow")}</div>
            <h1 className="mt-4 font-serif text-6xl md:text-8xl tracking-tight text-stone-900 leading-[0.9]">{t("maint.hero.title")}</h1>
            <p className="mt-6 text-lg text-stone-600 max-w-xl">{t("maint.hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="maint-hero-cta" className="btn-primary group">
                {t("hero.cta_primary")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-stone-200">
              <img src={IMAGES.service_maintenance} alt="Maintenance" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* TYPES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl mb-14">
          <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">service</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-stone-900 leading-tight">{t("maint.types.title")}</h2>
        </div>
        <div className="grid gap-6 md:gap-8 md:grid-cols-3">
          {TYPES.map((tp, i) => (
            <article
              key={tp.key}
              data-testid={`maint-type-${tp.key}`}
              className="group flex flex-col rounded-lg border border-stone-200 bg-white overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-transform duration-300"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={tp.image} alt={t(`maint.${tp.key}.title`)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-5 right-5">
                  <div className="text-white/80 text-[10px] tracking-[0.22em] uppercase font-mono">0{i + 1}</div>
                  <h3 className="mt-1 font-serif text-3xl md:text-4xl text-white leading-tight">{t(`maint.${tp.key}.title`)}</h3>
                </div>
              </div>
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <ul className="space-y-3 flex-1">
                  {tp.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-stone-700">
                      <CheckCircle2 size={16} className="mt-0.5 text-[#B95736] flex-shrink-0" strokeWidth={1.8} />
                      <span className="text-sm leading-relaxed">{t(`maint.${tp.key}.${f}`)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-stone-200 flex items-start gap-2">
                  <Target size={16} className="mt-0.5 text-[#B95736] flex-shrink-0" strokeWidth={1.8} />
                  <p className="text-sm text-stone-800 leading-relaxed italic">{t(`maint.${tp.key}.goal`)}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* PLANS */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">abonnements</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-stone-900 leading-tight">{t("maint.plans.title")}</h2>
            <p className="mt-6 text-stone-600 text-lg">{t("maint.plans.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <div
                key={p.key}
                data-testid={`maint-plan-${p.key}`}
                className={`relative flex flex-col rounded-lg border-2 ${p.ring} bg-white p-8 ${p.featured ? "md:-translate-y-2 shadow-xl" : ""}`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-[#B95736] text-white px-3 py-1 text-[10px] tracking-widest uppercase font-mono">
                    <Star size={11} strokeWidth={2.4} /> Populaire
                  </span>
                )}
                <div className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-[10px] tracking-widest uppercase font-mono ${p.chip}`}>
                  {t(`maint.plans.${p.key}.name`)}
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-serif text-6xl text-stone-900 leading-none">{t(`maint.plans.${p.key}.freq`)}</span>
                  <span className="text-xs text-stone-500 font-mono uppercase tracking-widest">{t("maint.plans.per_week")}</span>
                </div>
                <div className="mt-8 pt-6 border-t border-stone-200">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono mb-3">{t("maint.plans.includes")}</div>
                  <p className="text-stone-700 leading-relaxed text-sm">{t(`maint.plans.${p.key}.inc`)}</p>
                </div>
                <Link to="/contact" data-testid={`maint-plan-cta-${p.key}`} className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors ${p.featured ? "bg-[#B95736] text-white hover:bg-[#9E482C]" : "border border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white"}`}>
                  {t("hero.cta_primary")} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section id="details" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 scroll-mt-24">
        <div className="max-w-2xl mb-14">
          <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">à la carte</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-stone-900 leading-tight">{t("maint.details.title")}</h2>
          <p className="mt-4 text-stone-600 text-lg">{t("maint.details.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {DETAILS.map((d) => {
            const Icon = d.icon;
            return (
              <div key={d.key} data-testid={`maint-detail-${d.key}`} className="p-8 rounded-lg border border-stone-200 bg-white">
                <div className="w-11 h-11 rounded-md bg-[#B95736]/10 flex items-center justify-center text-[#B95736]">
                  <Icon size={20} strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-stone-900">{t(`maint.details.${d.key}.title`)}</h3>
                <p className="mt-3 text-stone-600 leading-relaxed">{t(`maint.details.${d.key}.desc`)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="rounded-2xl bg-[#1C1917] text-white p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">{t("quote.title")}</h3>
            <p className="mt-3 text-stone-300 max-w-lg">{t("quote.subtitle")}</p>
          </div>
          <Link to="/contact" data-testid="maint-footer-cta" className="btn-primary">
            {t("hero.cta_primary")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
